import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertTriangleIcon,
  BuildingIcon,
  FileIcon,
  ImageIcon,
  KeyIcon,
  MapPinIcon,
  NavigationIcon,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import {
  crewPropertyAddressLine,
  fetchCrewPropertyDetail,
  type CrewPropertyDetail,
} from '../api/crewProperties'
import { resolveEmployeeIdForUser } from '../lib/timeClockActions'
import {
  MobileEmptyState,
  MobilePage,
  MobilePageHeader,
  MobileSection,
  MobileStatusBadge,
  mobile,
} from '../components/mobile'

function mapsUrls(address: string) {
  if (!address) return { navigate: null as string | null, embed: null as string | null }
  return {
    navigate: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`,
    embed: `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`,
  }
}

function JobRows({
  jobs,
  empty,
}: {
  jobs: CrewPropertyDetail['todayJobs']
  empty: string
}) {
  if (!jobs.length) {
    return <p className="text-sm text-gray-500">{empty}</p>
  }
  return (
    <div className={`${mobile.listGroup}`}>
      {jobs.map((job) => (
        <Link
          key={job.id}
          to={`/jobs/details/${job.id}`}
          className={`${mobile.row} flex items-start justify-between gap-2`}
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {job.title || job.job_number || 'Job'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {[job.scheduled_date, job.scheduled_start_time].filter(Boolean).join(' · ')}
            </p>
          </div>
          {job.status && <MobileStatusBadge status={job.status} />}
        </Link>
      ))}
    </div>
  )
}

export function CrewPropertyDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [denied, setDenied] = useState(false)
  const [detail, setDetail] = useState<CrewPropertyDetail | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!user?.id || !id) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setDenied(false)
        const empId = await resolveEmployeeIdForUser(user.id)
        if (!empId) {
          if (!cancelled) {
            setDetail(null)
            setDenied(true)
          }
          return
        }
        const row = await fetchCrewPropertyDetail(empId, id)
        if (!cancelled) {
          if (!row) {
            setDetail(null)
            setDenied(true)
          } else {
            setDetail(row)
          }
        }
      } catch {
        if (!cancelled) {
          setDetail(null)
          setDenied(true)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [user?.id, id])

  const goBack = () => {
    if (returnTo) navigate(decodeURIComponent(returnTo))
    else navigate('/my-properties')
  }

  if (loading) {
    return (
      <MobilePage>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </MobilePage>
    )
  }

  if (denied || !detail) {
    return (
      <MobilePage>
        <MobilePageHeader title="Property" onBack={goBack} />
        <MobileEmptyState
          icon={<BuildingIcon className="w-12 h-12" />}
          title="Property unavailable"
          description="This site is not linked to your assigned or upcoming jobs."
        />
      </MobilePage>
    )
  }

  const address = crewPropertyAddressLine(detail)
  const { navigate: mapsNav, embed: mapsEmbed } = mapsUrls(address || detail.name || '')
  const title = detail.name || address || 'Property'

  // Future: when product opts in, render detail.customerContact here.
  const showCustomerContact = Boolean(detail.customerContact)

  return (
    <MobilePage>
      <MobilePageHeader title={title} subtitle={detail.name ? address : undefined} onBack={goBack} />

      {/* Address / map / navigate */}
      <section className={`${mobile.surface} overflow-hidden`}>
        {mapsEmbed && (
          <div className="relative h-40 bg-sky-50 border-b border-gray-100">
            <iframe
              title="Property map"
              src={mapsEmbed}
              className="absolute inset-0 w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
        <div className="p-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex items-start gap-2">
            <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 break-words">
              {address || 'No address on file'}
            </p>
          </div>
          {mapsNav && (
            <a
              href={mapsNav}
              target="_blank"
              rel="noopener noreferrer"
              className={`${mobile.secondaryBtn} !min-h-[40px] !px-3 !py-2 flex-shrink-0`}
            >
              <NavigationIcon className="w-4 h-4" />
              Navigate
            </a>
          )}
        </div>
      </section>

      {/* Gate codes / lockbox / access */}
      <MobileSection title="Access" flush>
        <div className="divide-y divide-gray-100">
          <div className={mobile.row}>
            <div className="flex items-start gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700 flex-shrink-0">
                <KeyIcon className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Access type
                </p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">
                  {detail.access_type || 'Not set'}
                </p>
                <p className="text-sm text-gray-600 mt-1 break-words">
                  {detail.access_info || 'No gate code / lockbox details on file'}
                </p>
              </div>
            </div>
          </div>
          {detail.additional_access.map((access, idx) => (
            <div key={idx} className={mobile.row}>
              <p className="text-sm font-medium text-gray-900">
                {access.location || 'Additional access'}
              </p>
              <p className="text-sm text-gray-600 mt-0.5">
                {[access.access_type, access.access_info].filter(Boolean).join(' — ')}
              </p>
            </div>
          ))}
        </div>
      </MobileSection>

      {/* Hazards / special instructions (property notes) */}
      <MobileSection title="Hazards & special instructions">
        {detail.siteNotes.length === 0 ? (
          <p className="text-sm text-gray-500">No site notes on file.</p>
        ) : (
          <div className="space-y-3">
            {detail.siteNotes.map((note) => (
              <div key={note.timestamp} className="flex gap-3">
                <AlertTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.note}</p>
                  {note.timestamp && note.timestamp !== new Date(0).toISOString() && (
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(note.timestamp).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </MobileSection>

      {/* Jobs */}
      <div className="space-y-2">
        <p className={mobile.sectionLabel}>Today&apos;s jobs</p>
        <JobRows jobs={detail.todayJobs} empty="No jobs at this property today." />
      </div>
      <div className="space-y-2">
        <p className={mobile.sectionLabel}>Upcoming jobs</p>
        <JobRows jobs={detail.upcomingJobs} empty="No upcoming jobs at this property." />
      </div>

      {/* Site photos / attachments from related jobs */}
      <MobileSection title="Site photos & attachments">
        {detail.attachments.length === 0 ? (
          <p className="text-sm text-gray-500">
            No photos or files from your jobs at this site yet.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {detail.attachments.map((att) => {
              const isImage =
                (att.mime_type || '').startsWith('image/') ||
                att.attachment_type === 'photo'
              return (
                <a
                  key={att.id}
                  href={att.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-xl border border-gray-200 overflow-hidden bg-white"
                >
                  {isImage ? (
                    <img
                      src={att.file_url}
                      alt={att.file_name || 'Site photo'}
                      className="w-full h-28 object-cover bg-gray-100"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-28 flex flex-col items-center justify-center gap-2 bg-gray-50 text-gray-500 px-2">
                      <FileIcon className="w-6 h-6" />
                      <span className="text-xs text-center truncate w-full">
                        {att.file_name || 'Attachment'}
                      </span>
                    </div>
                  )}
                  {isImage && (
                    <div className="px-2 py-1.5 flex items-center gap-1 text-[11px] text-gray-500">
                      <ImageIcon className="w-3 h-3" />
                      <span className="truncate">{att.file_name || 'Photo'}</span>
                    </div>
                  )}
                </a>
              )
            })}
          </div>
        )}
      </MobileSection>

      {showCustomerContact && detail.customerContact && (
        <MobileSection title="Customer contact">
          <div className={`${mobile.surface} p-4 space-y-1`}>
            {detail.customerContact.company_name && (
              <p className="text-sm font-medium text-gray-900">
                {detail.customerContact.company_name}
              </p>
            )}
            {detail.customerContact.phone && (
              <a
                href={`tel:${detail.customerContact.phone}`}
                className="block text-sm text-blue-600"
              >
                {detail.customerContact.phone}
              </a>
            )}
            {detail.customerContact.email && (
              <a
                href={`mailto:${detail.customerContact.email}`}
                className="block text-sm text-blue-600 break-all"
              >
                {detail.customerContact.email}
              </a>
            )}
          </div>
        </MobileSection>
      )}
    </MobilePage>
  )
}

export default CrewPropertyDetail
